import { makeAutoObservable } from 'mobx';

export type PendingIntegrationThread = {
  toolkitName: string;
  title: string;
  firstMessage: string;
};

const COACH_MARK_STORAGE_KEY = 'web-onboarding-coach-mark';

/**
 * Coach marks shown after onboarding, in order: first one pointing at the
 * created threads in the sidebar, then one pointing at New Chat.
 */
type CoachMarkStage = 'threads' | 'new-chat' | 'done';

const COACH_MARK_STAGES: CoachMarkStage[] = ['threads', 'new-chat', 'done'];

type PersistedCoachMark = {
  threadIds: string[];
  stage: CoachMarkStage;
};

const readPersistedCoachMark = (): PersistedCoachMark | null => {
  const raw = sessionStorage.getItem(COACH_MARK_STORAGE_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as PersistedCoachMark;
    if (!Array.isArray(parsed.threadIds)) return null;
    if (!COACH_MARK_STAGES.includes(parsed.stage)) return null;
    return {
      threadIds: parsed.threadIds.filter(
        (id): id is string => typeof id === 'string'
      ),
      stage: parsed.stage,
    };
  } catch {
    return null;
  }
};

const writePersistedCoachMark = (state: PersistedCoachMark): void => {
  sessionStorage.setItem(COACH_MARK_STORAGE_KEY, JSON.stringify(state));
};

/**
 * Bridge between the onboarding screen and the app shell: onboarding stores
 * which integrations the user set up, the chats screen consumes them on
 * arrival to spin up one query thread per integration, and the sidebar coach
 * mark points at the created threads until dismissed.
 */
class WebOnboardingThreadsStore {
  pendingThreads: PendingIntegrationThread[] = [];
  threadIds: string[] = [];
  coachMarkStage: CoachMarkStage = 'done';

  constructor() {
    makeAutoObservable(this);
    const persisted = readPersistedCoachMark();
    if (persisted) {
      this.threadIds = persisted.threadIds;
      this.coachMarkStage = persisted.stage;
    }
  }

  setPendingThreads(entries: PendingIntegrationThread[]): void {
    this.pendingThreads = entries;
  }

  /** Reads and clears the pending entries, so creation runs at most once. */
  consumePendingThreads(): PendingIntegrationThread[] {
    const pending = this.pendingThreads;
    this.pendingThreads = [];
    return pending;
  }

  setThreads(threadIds: string[]): void {
    this.threadIds = threadIds;
    this.setCoachMarkStage('threads');
  }

  dismissThreadsCoachMark(): void {
    this.setCoachMarkStage('new-chat');
  }

  dismissNewChatCoachMark(): void {
    this.setCoachMarkStage('done');
  }

  private setCoachMarkStage(stage: CoachMarkStage): void {
    this.coachMarkStage = stage;
    writePersistedCoachMark({ threadIds: this.threadIds, stage });
  }

  get coachMarkThreadId(): string | null {
    if (this.coachMarkStage !== 'threads') return null;
    return this.threadIds[0] ?? null;
  }

  get showNewChatCoachMark(): boolean {
    return this.coachMarkStage === 'new-chat';
  }
}

export const webOnboardingThreadsStore = new WebOnboardingThreadsStore();

export function clearWebOnboardingCoachMarkStorage(): void {
  sessionStorage.removeItem(COACH_MARK_STORAGE_KEY);
}
